/**
 * Attendance: one present/absent/late/half-day/on-leave record per employee per day.
 *
 * Lite slice of PRD P2-05 -- see `attendance_models.py`'s module docstring. Routes are
 * gated on the `leave` matrix cell, which grants `hr_manager`/`super_admin` Full and
 * `field_rep`/`store_staff`/`employee` Create+Read -- but that Create grant is for
 * `checkIn`/`checkOut` only. `markAttendance`/`updateAttendance` (marking or correcting
 * someone else's day) are narrowed to HR underneath that grant on the server
 * (`attendance_service._require_attendance_manage`), so calling them as anyone else comes
 * back as a plain 422/403, not a hidden button that silently does nothing.
 */

import { API_PREFIX, apiBlob, apiFetch, apiGet, type Page } from '@/api/client'
import type { ExportFormat } from '@/components/ExportButton'

export const ATTENDANCE_STATUSES = ['present', 'absent', 'late', 'half_day', 'on_leave'] as const
export type AttendanceStatus = (typeof ATTENDANCE_STATUSES)[number]

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  present: 'Present',
  absent: 'Absent',
  late: 'Late',
  half_day: 'Half day',
  on_leave: 'On leave',
}

export interface AttendanceMark {
  employee_id: string
  date: string
  status?: AttendanceStatus
  check_in_at?: string | null
  check_out_at?: string | null
  notes?: string | null
}

export type AttendanceUpdate = Partial<Omit<AttendanceMark, 'employee_id' | 'date'>>

export interface AttendanceRead {
  id: string
  employee_id: string
  employee_name: string | null
  location_id: string | null
  location_name: string | null
  date: string
  status: AttendanceStatus
  check_in_at: string | null
  check_out_at: string | null
  marked_by: string | null
  marked_by_name: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export interface AttendanceFilters {
  page?: number
  employee_id?: string
  location_id?: string
  date_from?: string
  date_to?: string
  status?: AttendanceStatus | ''
}

const BASE = `${API_PREFIX}/attendance`

function attendanceQuery(filters: AttendanceFilters): string {
  const params = new URLSearchParams()
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  if (filters.employee_id) params.set('employee_id', filters.employee_id)
  if (filters.location_id) params.set('location_id', filters.location_id)
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  if (filters.status) params.set('status', filters.status)
  const qs = params.toString()
  return qs ? `?${qs}` : ''
}

export const listAttendance = (filters: AttendanceFilters = {}) =>
  apiFetch<Page<AttendanceRead>>(`${BASE}${attendanceQuery(filters)}`)

export const getAttendance = (id: string) => apiGet<AttendanceRead>(`${BASE}/${id}`)

export const markAttendance = (data: AttendanceMark) =>
  apiGet<AttendanceRead>(BASE, { method: 'POST', body: JSON.stringify(data) })

export const updateAttendance = (id: string, data: AttendanceUpdate) =>
  apiGet<AttendanceRead>(`${BASE}/${id}`, { method: 'PATCH', body: JSON.stringify(data) })

/** No body -- the server resolves the caller's own employee record and today's date. */
export const checkIn = () => apiGet<AttendanceRead>(`${BASE}/check-in`, { method: 'POST' })

export const checkOut = () => apiGet<AttendanceRead>(`${BASE}/check-out`, { method: 'POST' })

export const exportAttendance = (filters: AttendanceFilters, format: ExportFormat) => {
  const params = new URLSearchParams({ format })
  if (filters.employee_id) params.set('employee_id', filters.employee_id)
  if (filters.location_id) params.set('location_id', filters.location_id)
  if (filters.date_from) params.set('date_from', filters.date_from)
  if (filters.date_to) params.set('date_to', filters.date_to)
  if (filters.status) params.set('status', filters.status)
  return apiBlob(`${BASE}/export?${params}`)
}
